import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { Card, CardTitle, CardBody } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Loading } from '../components/ui/Loading';
import { Button } from '../components/ui/Button';
import { Table, TableHead, TableBody, TableRow, TableCell } from '../components/ui/Table';
import {
  ArrowLeft,
  DollarSign,
  RefreshCw,
  CreditCard,
  Layers,
  Users,
  Building2,
  ToggleLeft,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import AccountUsageTopUps from '../components/account-usage-top-ups';
import type { PlanDefinition } from '../types/admin';

/**
 * One line describing a plan in the picker.
 *
 * The metering model leads, because it is the part of a plan change an admin
 * most needs to see before making it: two plans can carry the same name and
 * price and still limit the account by completely different things.
 */
function describePlan(plan: PlanDefinition): string {
  const detail =
    plan.metering === 'tokens'
      ? `${(plan.monthly_tokens ?? 0).toLocaleString()} tokens/mo`
      : plan.is_custom
        ? 'custom limits'
        : `${plan.limits.scans} scans, ${plan.limits.pr_reviews} PRs, ${plan.limits.repos} repos`;
  const extras = plan.capabilities?.length ? ` + ${plan.capabilities.join(', ')}` : '';
  return `${plan.name} [${plan.slug}] — ${detail}${extras}`;
}

const FLAG_META: Record<string, { label: string; description: string }> = {
  external_agent_firewall: {
    label: 'External Agent Firewall',
    description: 'Enable firewall protection for external AI agents',
  },
};

const AccountDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAddBalance, setShowAddBalance] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceReason, setBalanceReason] = useState('');
  const [showSetPlan, setShowSetPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [planReason, setPlanReason] = useState('');

  const { data: account } = useQuery({
    queryKey: ['account-details', id],
    queryFn: () => adminApiClient.getAccountDetails(id!),
    enabled: !!id,
  });

  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['account-balance', id],
    queryFn: () => adminApiClient.getAccountBalance(id!),
    enabled: !!id,
  });

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ['account-transactions', id],
    queryFn: () => adminApiClient.getTransactions({ account_id: id, page_size: 10 }),
    enabled: !!id,
  });

  const { data: planData, isLoading: planLoading } = useQuery({
    queryKey: ['account-plan', id],
    queryFn: () => adminApiClient.getAccountPlan(id!),
    enabled: !!id,
  });

  // Cached for the session: the registry is loaded from config at API start-up
  // and cannot change without a deploy, so refetching it per visit buys nothing.
  const {
    data: plans,
    isLoading: plansLoading,
    isError: plansFailed,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: () => adminApiClient.getPlans(),
    staleTime: Infinity,
  });

  const selectedPlanDefinition = plans?.plans.find((p) => p.slug === selectedPlan);
  const currentPlanDefinition = plans?.plans.find(
    (p) => p.slug === planData?.plan.slug,
  );
  // A controlled select whose value matches no option renders the first option
  // as highlighted while state keeps the unmatched slug — so the admin reads
  // "Free" and submits the old slug. Surfaced rather than silently corrected,
  // because an account holding a slug the registry no longer lists is itself
  // worth seeing.
  const currentSlugMissing =
    !!planData && !!plans && !currentPlanDefinition;
  // Compares the two plan *definitions*, not the account's usage record against
  // the target. Those can disagree mid-cycle — right after a change, before the
  // cycle rolls — which fired the warning on a no-op selection.
  const crossesMeteringModels =
    !!currentPlanDefinition &&
    !!selectedPlanDefinition &&
    (currentPlanDefinition.metering ?? 'counts') !==
      (selectedPlanDefinition.metering ?? 'counts');

  const { data: featuresData, isLoading: featuresLoading } = useQuery({
    queryKey: ['account-features', id],
    queryFn: () => adminApiClient.getAccountFeatures(id!),
    enabled: !!id,
  });


  const addBalanceMutation = useMutation({
    mutationFn: (data: { amount_cents: number; reason: string }) =>
      adminApiClient.addAccountBalance(id!, data),
    onSuccess: (data) => {
      toast.success(`Balance added successfully. New balance: $${(data.new_balance_cents / 100).toFixed(2)}`);
      setShowAddBalance(false);
      setBalanceAmount('');
      setBalanceReason('');
      queryClient.invalidateQueries({ queryKey: ['account-balance', id] });
      queryClient.invalidateQueries({ queryKey: ['account-transactions', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add balance');
    },
  });

  const setPlanMutation = useMutation({
    mutationFn: (data: { plan_slug: string; reason: string }) =>
      adminApiClient.setAccountPlan(id!, data),
    onSuccess: (data) => {
      toast.success(`Plan updated to ${data.plan?.name || 'new plan'}`);
      setShowSetPlan(false);
      setPlanReason('');
      queryClient.invalidateQueries({ queryKey: ['account-plan', id] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update plan');
    },
  });

  const toggleFeatureMutation = useMutation({
    mutationFn: ({ feature, enabled }: { feature: string; enabled: boolean }) =>
      adminApiClient.setAccountFeature(id!, feature, enabled),
    onSuccess: (data) => {
      toast.success(`${data.feature} ${data.enabled ? 'enabled' : 'disabled'}`);
      queryClient.invalidateQueries({ queryKey: ['account-features', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update feature flag');
    },
  });

  const handleAddBalance = (e: React.FormEvent) => {
    e.preventDefault();
    const amountDollars = parseFloat(balanceAmount);
    if (isNaN(amountDollars) || amountDollars <= 0) {
      toast.error('Please enter a valid positive amount');
      return;
    }
    if (!balanceReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    addBalanceMutation.mutate({
      amount_cents: Math.round(amountDollars * 100),
      reason: balanceReason.trim(),
    });
  };


  const handleSetPlan = (e: React.FormEvent) => {
    e.preventDefault();
    if (!planReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }
    setPlanMutation.mutate({
      plan_slug: selectedPlan,
      reason: planReason.trim(),
    });
  };

  const formatCents = (cents: number) => {
    const sign = cents < 0 ? '-' : '';
    return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      payment: 'Payment',
      auto_reload: 'Auto Reload',
      usage_deduction: 'Usage',
      refund: 'Refund',
      adjustment: 'Adjustment',
      admin_credit: 'Admin Credit',
    };
    return labels[type] || type;
  };

  const getTransactionVariant = (type: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    const variants: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
      payment: 'success',
      auto_reload: 'success',
      usage_deduction: 'warning',
      refund: 'info',
      adjustment: 'default',
      admin_credit: 'success',
    };
    return variants[type] || 'default';
  };

  if (balanceLoading) {
    return <Loading message="Loading account details..." />;
  }

  if (!balance) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/admin/accounts')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardBody>
            <p className="text-gray-400">Account not found</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate('/admin/accounts')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-white">{account?.name || 'Account Details'}</h1>
            </div>
            <p className="text-gray-400 font-mono text-sm mt-1">{id}</p>
          </div>
        </div>
      </div>

      {/* Account Info Card */}
      {account && (
        <Card>
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <Building2 className="w-5 h-5 text-gray-400" />
              <CardTitle>Account Information</CardTitle>
            </div>
          </div>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <span className="text-gray-400 text-sm">Account Name</span>
                <p className="text-white font-medium mt-1">{account.name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Clerk Org ID</span>
                <p className="text-white font-mono text-sm mt-1">{account.clerk_org_id || <span className="text-yellow-400">Unlinked</span>}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Created</span>
                <p className="text-white mt-1">{format(new Date(account.created_at), 'MMM d, yyyy')}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Members</span>
                <p className="text-white mt-1">{account.members?.length || 0}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Members Table */}
      {account && (
        <Card padding="none">
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-gray-400" />
              <CardTitle>Members ({account.members?.length || 0})</CardTitle>
            </div>
          </div>
          {account.members && account.members.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell header>Name</TableCell>
                  <TableCell header>Email</TableCell>
                  <TableCell header>GitHub</TableCell>
                  <TableCell header>Role</TableCell>
                  <TableCell header>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {account.members.map((member) => (
                  <TableRow key={member.user_id}>
                    <TableCell>
                      <span className="font-medium text-white">{member.name || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{member.email}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-300">{member.github_username || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.role === 'owner' ? 'pro' : 'default'}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-400">
                        {format(new Date(member.joined_at), 'MMM d, yyyy')}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="p-12 text-center text-gray-400">
              No members found
            </div>
          )}
        </Card>
      )}

      {/* Balance Card */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 border-b border-gray-700">
            <CardTitle>Balance Information</CardTitle>
          </div>
          <CardBody>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Current Balance</span>
                <span className="text-2xl font-bold text-white">
                  {formatCents(balance.balance_cents)}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Add Balance Card */}
        <Card>
          <div className="p-6 border-b border-gray-700">
            <CardTitle>Add Balance</CardTitle>
          </div>
          <CardBody>
            {showAddBalance ? (
              <form onSubmit={handleAddBalance} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Amount (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={balanceAmount}
                      onChange={(e) => setBalanceAmount(e.target.value)}
                      className="w-full pl-8 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="50.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    value={balanceReason}
                    onChange={(e) => setBalanceReason(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Customer support credit"
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={addBalanceMutation.isPending}
                    disabled={addBalanceMutation.isPending}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Add Balance
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowAddBalance(false);
                      setBalanceAmount('');
                      setBalanceReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Add credit to this account</p>
                <Button variant="primary" onClick={() => setShowAddBalance(true)}>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Add Balance
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Plan Management */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 border-b border-gray-700">
            <CardTitle>Current Plan</CardTitle>
          </div>
          <CardBody>
            {planLoading ? (
              <div className="py-8 text-center">
                <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
              </div>
            ) : planData ? (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Plan</span>
                  <Badge variant={planData.plan.slug !== 'free' ? 'pro' : 'default'}>
                    {planData.plan.name} ({planData.plan.slug})
                  </Badge>
                </div>
                {/*
                  The metering model is stated rather than inferred from which
                  numbers are present. On a token plan the count figures below
                  still advance but no longer limit anything, so reading them as
                  ceilings would describe a limit that will never be enforced.
                */}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Metered on</span>
                  <span className="text-gray-300 text-sm">
                    {planData.usage.metering === 'tokens' ? 'Tokens' : 'Counts'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Includes</span>
                  <span className="text-gray-300 text-sm">
                    {planData.plan.capabilities?.length
                      ? planData.plan.capabilities.join(', ')
                      : 'No restricted surfaces'}
                  </span>
                </div>
                {planData.usage.tokens && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Tokens</span>
                    <span className="text-gray-300 text-sm">
                      {planData.usage.tokens.consumed.toLocaleString()} /{' '}
                      {planData.usage.tokens.allocation.toLocaleString()}
                      {' · '}
                      {planData.usage.tokens.remaining.toLocaleString()} left
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Scans</span>
                  <span className="text-gray-300 text-sm">
                    {planData.usage.scans_used} / {planData.usage.scans_limit}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">PR Reviews</span>
                  <span className="text-gray-300 text-sm">
                    {planData.usage.pr_reviews_used} / {planData.usage.pr_reviews_limit}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Repos</span>
                  <span className="text-gray-300 text-sm">
                    {planData.usage.repos_used} / {planData.usage.repos_limit}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Billing Cycle</span>
                  <span className="text-gray-300 text-sm">
                    {format(new Date(planData.usage.cycle_start), 'MMM d')} - {format(new Date(planData.usage.cycle_end), 'MMM d, yyyy')}
                  </span>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-400">
                No plan assigned
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <div className="p-6 border-b border-gray-700">
            <CardTitle>Change Plan</CardTitle>
          </div>
          <CardBody>
            {showSetPlan ? (
              <form onSubmit={handleSetPlan} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Select Plan
                  </label>
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    disabled={plansLoading || plansFailed}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {/*
                      The account's own slug, when the registry does not list it.
                      Without this the select shows the first plan while holding
                      the real one, and Update posts a slug the admin never saw.
                    */}
                    {currentSlugMissing && (
                      <option value={selectedPlan} disabled>
                        {selectedPlan} — not in the plan registry, pick one below
                      </option>
                    )}
                    {(plans?.plans ?? []).map((plan) => (
                      <option key={plan.slug} value={plan.slug}>
                        {describePlan(plan)}
                      </option>
                    ))}
                  </select>
                </div>
                {plansFailed && (
                  <div className="rounded-md border border-red-700 bg-red-900/30 px-4 py-3 text-sm text-red-200">
                    Could not load the plan list, so there is nothing safe to pick
                    from. Reload before changing this account&apos;s plan.
                  </div>
                )}
                {/*
                  Warned, not blocked. Moving between metering models changes
                  what limits the account -- a token plan stops enforcing scan
                  and PR counts, a count plan stops enforcing the allocation --
                  and that is a deliberate operation, not a mistake to prevent.
                */}
                {crossesMeteringModels && (
                  <div className="rounded-md border border-yellow-700 bg-yellow-900/30 px-4 py-3 text-sm text-yellow-200">
                    This changes how the account is metered, from{' '}
                    {currentPlanDefinition?.metering === 'tokens' ? 'tokens' : 'counts'} to{' '}
                    {selectedPlanDefinition?.metering === 'tokens' ? 'tokens' : 'counts'}.
                    Allocations already recorded for closed months are unaffected.
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Reason
                  </label>
                  <input
                    type="text"
                    value={planReason}
                    onChange={(e) => setPlanReason(e.target.value)}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Customer upgraded via Stripe"
                    required
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    type="submit"
                    variant="primary"
                    loading={setPlanMutation.isPending}
                    disabled={
                      setPlanMutation.isPending ||
                      plansLoading ||
                      plansFailed ||
                      !selectedPlanDefinition
                    }
                  >
                    <Layers className="w-4 h-4 mr-2" />
                    Update Plan
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      setShowSetPlan(false);
                      setPlanReason('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="text-center py-8">
                <Layers className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400 mb-4">Change account plan</p>
                <Button
                  variant="primary"
                  onClick={() => {
                    setSelectedPlan(planData?.plan.slug || 'free');
                    setShowSetPlan(true);
                  }}
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Change Plan
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Usage Top-ups */}
      <AccountUsageTopUps accountId={id!} />

      {/* Feature Flags */}
      <Card>
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <ToggleLeft className="w-5 h-5 text-gray-400" />
              <CardTitle>Feature Flags</CardTitle>
            </div>
            {featuresData?.features && (
              <span className="text-gray-500 text-sm">
                {Object.values(featuresData.features).filter(Boolean).length} of{' '}
                {Object.keys(featuresData.features).length} enabled
              </span>
            )}
          </div>
        </div>
        <CardBody>
          {featuresLoading ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
            </div>
          ) : featuresData?.features && Object.keys(featuresData.features).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(featuresData.features).map(([flag, enabled]) => {
                const meta = FLAG_META[flag];
                return (
                  <div
                    key={flag}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      enabled
                        ? 'bg-primary-500/5 border-primary-500/30'
                        : 'bg-gray-800/50 border-gray-700'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {enabled ? (
                        <CheckCircle2 className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <p className="text-white font-medium text-sm">
                          {meta?.label || flag}
                        </p>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {meta?.description || flag}
                        </p>
                        <p className="text-gray-600 text-xs font-mono mt-1">{flag}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleFeatureMutation.mutate({ feature: flag, enabled: !enabled })}
                      disabled={toggleFeatureMutation.isPending}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                        enabled ? 'bg-primary-500' : 'bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-400">
              No feature flags configured
            </div>
          )}
        </CardBody>
      </Card>

      {/* Recent Transactions */}
      <Card padding="none">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <CardTitle>Recent Transactions</CardTitle>
          <Link to={`/admin/transactions?account_id=${id}`}>
            <Button variant="ghost" size="sm">
              View All
            </Button>
          </Link>
        </div>

        {transactionsLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin mx-auto" />
          </div>
        ) : transactions?.data?.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell header>Type</TableCell>
                <TableCell header>Amount</TableCell>
                <TableCell header>Balance After</TableCell>
                <TableCell header>Description</TableCell>
                <TableCell header>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.data.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell>
                    <Badge variant={getTransactionVariant(tx.type)}>
                      {getTransactionTypeLabel(tx.type)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className={tx.amount_cents < 0 ? 'text-red-400' : 'text-green-400'}>
                      {formatCents(tx.amount_cents)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-300">{formatCents(tx.balance_after_cents)}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-400 text-sm">{tx.description}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-gray-400">
                      {format(new Date(tx.created_at), 'MMM d, HH:mm')}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="p-12 text-center text-gray-400">
            No transactions found
          </div>
        )}
      </Card>
    </div>
  );
};

export default AccountDetails;
